import type { PropsWithChildren, FunctionComponent } from 'react'
import { useMemo } from 'react'
import { type ApolloError } from '@apollo/client'
import LoadingDots from '../../../../../components/LoadingDots'
import Table from '../../../../../components/Table/Table'
import { useStoreGroupListTableColumns } from '../utils/tableColumns.hooks'
import { StoreGroupsLoadError } from '../../error/StoreGroupsLoadError'
import { type StoreGroup } from '../../../../../common/graphql-types/retailerLocationGroup.types'
import { StoreGroupsListEmptyState } from './StoreGroupsListEmptyState'
import { usePartnerId } from '../../../../../../utils/routing/params.hooks'
import { type Retailer, useGetRetailersForPartnerQuery } from '../../../../../../__codegen__/api'
import { truncateStr } from '../../../../../common/utils'
import { useDashMessages } from '../../../../../../intl/intl.hooks'

export interface StoreGroupsListBodyProps {
  storeGroups: StoreGroup[]
  storeGroupIdOrNameFilter?: string
  loading: boolean
  error?: ApolloError
  onEditClicked?: (storeGroupId: string) => void
}
export const StoreGroupsListBody: FunctionComponent<
  PropsWithChildren<StoreGroupsListBodyProps>
> = ({ storeGroups, loading, error, onEditClicked, storeGroupIdOrNameFilter }) => {
  const columns = useStoreGroupListTableColumns({ onEditClicked })
  const messages = useDashMessages({
    all: 'settings.storeGroups.table.columns.banners.all',
  })

  const partnerId = usePartnerId()

  const {
    data: partnerData,
    error: partnerError,
    loading: partnerLoading,
  } = useGetRetailersForPartnerQuery({
    variables: { partnerId: partnerId },
  })

  const storeGroupsWithBannerNames = useMemo(() => {
    const allRetailers = partnerData?.partner?.retailers || []
    const retailersById: Record<string, Retailer> = allRetailers.reduce((acc, r) => {
      acc[r.id] = r
      return acc
    }, {})

    const filteredStoreGroups = storeGroupIdOrNameFilter
      ? storeGroups.filter(sg => {
          return (
            sg.name.toLowerCase().includes(storeGroupIdOrNameFilter.toLowerCase()) ||
            sg.id.toString().includes(storeGroupIdOrNameFilter)
          )
        })
      : storeGroups

    return filteredStoreGroups?.map(sg => {
      const retailers = sg.retailerIds?.map(id => retailersById[id]).filter(r => !!r)

      let bannerNames = ''
      const bannerNamesLong = retailers.map(r => r.name).join(', ')
      if (retailers.length === 1) {
        const [{ name }] = retailers
        bannerNames = truncateStr(name, 15)
      } else if (retailers.length === allRetailers.length) {
        bannerNames = messages.all
      } else if (retailers.length > 1) {
        bannerNames = `${truncateStr(retailers[0].name, 15)} + ${retailers.length - 1} more`
      }

      return {
        ...sg,
        bannerNames,
        bannerNamesLong,
      }
    })
  }, [partnerData?.partner?.retailers, storeGroups, messages.all, storeGroupIdOrNameFilter])

  if (error || partnerError) {
    return <StoreGroupsLoadError />
  }

  if (loading || partnerLoading) {
    return <LoadingDots centered />
  }

  if (storeGroups.length === 0) {
    return <StoreGroupsListEmptyState />
  }

  return <Table columns={columns} data={storeGroupsWithBannerNames} paginated />
}
